import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { SaveResponse } from 'src/app/core/models/save-response';
import { TenantCollectionResponse } from 'src/app/core/models/tenant-collection-response';
import { TenantIngestionJobRequest } from 'src/app/core/models/tenant-ingestion-job-request';
import { TenantRequest } from 'src/app/core/models/tenant-request';
import { TenantScheduleRequest } from 'src/app/core/models/tenant-schedule-request';
import { TenantScheduleResponse } from 'src/app/core/models/tenant-schedule-response';
import { TenantSourceEntityResponse } from 'src/app/core/models/tenant-source-entity-response';
import { TenantIngestionIdentifiers } from 'src/app/shared/models/tenant-ingestion-identifiers';
import { TenantScheduleSummary } from 'src/app/shared/models/tenant-schedule-summary';
import { environment } from 'src/environments/environment';
import { EntityRequestV3 } from '../../models/entity-request-v3';
import { EntityResponseV3 } from '../../models/entity-response-v3';
import { SummaryTenantSourceEntityResponse } from '../../models/summary-tenant-source-entity-response';
import { TenantIngestionJobResponse } from '../../models/tenant-ingestion-job-response';
import { TenantResponse } from '../../models/tenant-response';
import { TenantResponseV3 } from '../../models/tenant-response-v3';
import { TenantScheduleJobResponse } from '../../models/tenant-schedule-job-response';
import { TenantScheduleSummaryResponse } from '../../models/tenant-schedule-summary-response';
import { TenantSourceRequest } from '../../models/tenant-source-request';
import { TenantSourceResponseV3 } from '../../models/tenant-source-response-v3';
import { TenantUpdateRequest } from '../../models/tenant-update-request';
import { UserPermissionResponse } from '../../models/user-permission-response';
import { TenantSelfHostedIrt } from 'src/app/shared/models/tenant-irt';
import { CronValidationResponse } from '../../models/cron-validation-response';

@Injectable({
    providedIn: 'root'
})
export class TenantApiService {
    baseUrl: string = environment.apiUrl;

    summaryTenantSourceEntitiesUrlSuffix = 'v2/tenants/{tenant_id}/sources/{source_id}/summaryentities';
    tenantCollectionsUrlSuffix = 'v1/tenants/{tenant_id}/collections';
    tenantScheduleJobsUrlSuffix = 'v1/tenants/{tenant_id}/schedules/{schedule_id}/jobs';
    tenantScheduleJobsV3UrlSuffix = 'v3/tenants/{tenant_id}/schedules/{schedule_id}';
    tenantScheduleSummariesUrlSuffix = 'v1/tenants/{tenant_id}/summarySchedules';
    tenantScheduleSummariesV3UrlSuffix = 'v3/tenants/{tenant_id}/summarySchedules';
    tenantSchedulesUrlSuffix = 'v1/tenants/{tenant_id}/schedules';
    tenantSourceEntitiesUrlSuffix = 'v1/tenants/{tenant_id}/sources/{source_id}/entities';
    tenantSourceEntityIngestionsUpdateUrlSuffix =
        'v2/tenants/{tenant_id}/sources/{source_id}/entities/{entity}/ingestions/{ingestion_id}';
    tenantSourceEntityIngestionsV2UrlSuffix =
        'v2/tenants/{tenant_id}/sources/{source_id}/entities/{entity_id}/ingestions';
    tenantSourceEntitiesV3UrlSuffix = 'v3/tenants/{tenant_id}/sources/{source_id}/entities';
    tenantSourceEntityV3UrlSuffix = 'v3/tenants/{tenant_id}/sources/{source_id}/entities/{entity_id}';
    tenantScheduleQueueActiveJobsSuffix = 'v3/tenants/{tenant_id}/schedules/{schedule_id}/queueActiveJobs';
    tenantSourcesUrlSuffix = 'v3/tenants/{tenant_id}/sources';
    tenantsUrlSuffix = 'v1/tenants';
    tenantsAdminUrlSuffix = 'v3/admin/tenants';
    tenantUrlSuffix = 'v1/tenants/{tenant_id}';
    tenantV3UrlSuffix = 'v3/tenants/{tenant_id}';
    tenantV3CreateUrlSuffix = 'v3/tenants';
    tenantIngestionsV3UrlSuffix = `${this.tenantV3UrlSuffix}/ingestions`;
    tenantSelfHostedIrtUrlSuffix = `${this.tenantV3UrlSuffix}/tenant_irt`;

    constructor(private httpClient: HttpClient) {}

    getTenant(tenantId: string) {
        const idSuffix = this.tenantUrlSuffix.replace('{tenant_id}', tenantId);
        return this.httpClient.get<TenantResponse>(`${this.baseUrl}${idSuffix}`);
    }

    getTenantCollections(tenantId: string) {
        const idSuffix = this.tenantCollectionsUrlSuffix.replace('{tenant_id}', tenantId);
        return this.httpClient.get<TenantCollectionResponse[]>(`${this.baseUrl}${idSuffix}`);
    }

    getTenants() {
        return this.httpClient.get<TenantResponse[]>(`${this.baseUrl}${this.tenantsUrlSuffix}`);
    }

    getTenantsForAdmin() {
        return this.httpClient.get<TenantResponseV3[]>(`${this.baseUrl}${this.tenantsAdminUrlSuffix}`);
    }

    getTenantScheduleJobs(tenantId: string, scheduleId: number) {
        const idSuffix = this.tenantScheduleJobsUrlSuffix
            .replace('{tenant_id}', tenantId)
            .replace('{schedule_id}', scheduleId.toString());
        return this.httpClient.get<TenantScheduleJobResponse[]>(`${this.baseUrl}${idSuffix}`);
    }

    saveTenantScheduleJobs(tenantId: string, scheduleId: number, jobs: number[]) {
        const idSuffix = this.tenantScheduleJobsV3UrlSuffix
            .replace('{tenant_id}', tenantId)
            .replace('{schedule_id}', scheduleId.toString());
        return this.httpClient.post<SaveResponse>(`${this.baseUrl}${idSuffix}/addToJobs`, {
            tenantIngestionIdList: jobs
        });
    }

    removeTenantScheduleJobs(tenantId: string, scheduleId: number, jobs: number[]) {
        const idSuffix = this.tenantScheduleJobsV3UrlSuffix
            .replace('{tenant_id}', tenantId)
            .replace('{schedule_id}', scheduleId.toString());
        return this.httpClient.post<SaveResponse>(`${this.baseUrl}${idSuffix}/removeJobs`, {
            tenantIngestionIdList: jobs
        });
    }

    /** Get all schedules for a tenant. Include summary detail about assigned jobs. */
    getTenantScheduleSummaries(tenantId: string) {
        const idSuffix = this.tenantScheduleSummariesUrlSuffix.replace('{tenant_id}', tenantId);
        return this.httpClient.get<TenantScheduleSummaryResponse[]>(`${this.baseUrl}${idSuffix}`);
    }

    /** Get all schedules for a tenant. No job detail. */
    getTenantSchedules(tenantId: string) {
        const idSuffix = this.tenantSchedulesUrlSuffix.replace('{tenant_id}', tenantId);
        return this.httpClient.get<TenantScheduleResponse[]>(`${this.baseUrl}${idSuffix}`);
    }

    getTenantSourceEntities(tenantId: string, sourceId: number) {
        const idSuffix = this.tenantSourceEntitiesUrlSuffix
            .replace('{tenant_id}', tenantId)
            .replace('{source_id}', sourceId.toString());
        return this.httpClient.get<TenantSourceEntityResponse[]>(`${this.baseUrl}${idSuffix}`);
    }

    getTenantSourceEntity(tenantId: string, sourceId: number, entityId: number) {
        const idSuffix = this.tenantSourceEntitiesUrlSuffix
            .replace('{tenant_id}', tenantId)
            .replace('{source_id}', sourceId.toString());
        return this.httpClient.get<TenantSourceEntityResponse>(`${this.baseUrl}${idSuffix}/${entityId}`);
    }

    getTenantSourceEntityIngestionsV2(
        tenantId: string,
        sourceId: number,
        entityId: number
    ): Observable<TenantIngestionJobResponse[]> {
        const idSuffix = this.tenantSourceEntityIngestionsV2UrlSuffix
            .replace('{tenant_id}', tenantId)
            .replace('{source_id}', sourceId.toString())
            .replace('{entity_id}', entityId.toString());
        return this.httpClient.get<TenantIngestionJobResponse[]>(`${this.baseUrl}${idSuffix}`);
    }

    getTenantSource(tenantId: string, sourceId: number) {
        const idSuffix = this.tenantSourcesUrlSuffix.replace('{tenant_id}', tenantId);
        return this.httpClient.get<TenantSourceResponseV3>(`${this.baseUrl}${idSuffix}/${sourceId}`);
    }

    getTenantSources(tenantId: string) {
        const idSuffix = this.tenantSourcesUrlSuffix.replace('{tenant_id}', tenantId);
        return this.httpClient.get<TenantSourceResponseV3[]>(`${this.baseUrl}${idSuffix}`);
    }

    createTenant(tenant: TenantRequest): Observable<SaveResponse> {
        return this.httpClient.post<SaveResponse>(`${this.baseUrl}${this.tenantV3CreateUrlSuffix}`, tenant);
    }

    updateTenant(tenantId: string, tenantUpdateRequest: TenantUpdateRequest): Observable<HttpResponse<SaveResponse>> {
        const tenantIdSuffix: string = `${this.baseUrl}${this.tenantV3UrlSuffix}`.replace('{tenant_id}', tenantId);

        return this.httpClient.put<SaveResponse>(tenantIdSuffix, tenantUpdateRequest, { observe: 'response' });
    }

    saveTenantIngestionJob(request: TenantIngestionJobRequest) {
        const idSuffix = this.tenantSourceEntityIngestionsV2UrlSuffix
            .replace('{tenant_id}', request.tenant_id)
            .replace('{source_id}', request.tenant_source_id.toString())
            .replace('{entity_id}', request.tenant_source_entity_id.toString());
        return this.httpClient.post<SaveResponse>(`${this.baseUrl}${idSuffix}`, request);
    }

    updateTenantIngestionJob(request: TenantIngestionJobRequest) {
        const idSuffix = this.tenantSourceEntityIngestionsV2UrlSuffix
            .replace('{tenant_id}', request.tenant_id)
            .replace('{source_id}', request.tenant_source_id.toString())
            .replace('{entity_id}', request.tenant_source_entity_id.toString())
            .replace('{ingestion_id}', (<number>request.tenant_ingestion_id).toString());
        return this.httpClient.post<string>(`${this.baseUrl}${idSuffix}`, request);
    }

    saveTenantSchedule(tenantSchedule: TenantScheduleRequest) {
        const idSuffix = this.tenantSchedulesUrlSuffix.replace('{tenant_id}', tenantSchedule.tenant_id);
        return this.httpClient.post<SaveResponse>(`${this.baseUrl}${idSuffix}`, tenantSchedule);
    }

    updateTenantSchedule(tenantSchedule: TenantScheduleRequest) {
        const idSuffix = this.tenantSchedulesUrlSuffix.replace('{tenant_id}', tenantSchedule.tenant_id);
        return this.httpClient.post<SaveResponse>(
            `${this.baseUrl}${idSuffix}/${tenantSchedule.tenant_schedule_id}`,
            tenantSchedule
        );
    }

    getTenantSchedule(tenantId: string, scheduleId: number) {
        const idSuffix = this.tenantScheduleSummariesV3UrlSuffix.replace('{tenant_id}', tenantId);
        return this.httpClient.get<TenantScheduleSummary>(`${this.baseUrl}${idSuffix}/${scheduleId}`);
    }

    createTenantSource(tenantSource: TenantSourceRequest) {
        const idSuffix = this.tenantSourcesUrlSuffix.replace('{tenant_id}', tenantSource.tenantId);
        return this.httpClient.post<SaveResponse>(`${this.baseUrl}${idSuffix}`, tenantSource);
    }

    updateTenantSource(tenantSource: TenantSourceRequest) {
        const idSuffix = this.tenantSourcesUrlSuffix.replace('{tenant_id}', tenantSource.tenantId);
        return this.httpClient.put<SaveResponse>(
            `${this.baseUrl}${idSuffix}/${tenantSource.tenantSourceId}`,
            tenantSource
        );
    }

    getSummaryTenantSourceEntities(
        tenantId: string,
        sourceId: number
    ): Observable<SummaryTenantSourceEntityResponse[]> {
        const idSuffix = this.summaryTenantSourceEntitiesUrlSuffix
            .replace('{tenant_id}', tenantId)
            .replace('{source_id}', sourceId.toString());
        return this.httpClient.get<SummaryTenantSourceEntityResponse[]>(`${this.baseUrl}${idSuffix}`);
    }

    getTenantSourceEntitiesV3(tenantId: string, sourceId: number): Observable<EntityResponseV3[]> {
        const entitiesSuffix: string = this.tenantSourceEntitiesV3UrlSuffix
            .replace('{tenant_id}', tenantId)
            .replace('{source_id}', sourceId.toString());

        return this.httpClient.get<EntityResponseV3[]>(`${this.baseUrl}${entitiesSuffix}`);
    }

    getTenantSourceEntityV3(tenantId: string, sourceId: number, entityId: number): Observable<EntityResponseV3> {
        const url: string = this.tenantSourceEntityV3UrlSuffix
            .replace('{tenant_id}', tenantId)
            .replace('{source_id}', sourceId.toString())
            .replace('{entity_id}', entityId.toString());

        return this.httpClient.get<EntityResponseV3>(`${this.baseUrl}${url}`);
    }

    upsertTenantSourceEntities(tenantId: string, sourceId: number, entities: EntityRequestV3[]): Observable<object> {
        const entitiesSuffix: string = this.tenantSourceEntitiesV3UrlSuffix
            .replace('{tenant_id}', tenantId)
            .replace('{source_id}', sourceId.toString());
        return this.httpClient.put(`${this.baseUrl}${entitiesSuffix}`, entities);
    }

    queueTenantScheduleJobs(tenantId: string, scheduleId: number) {
        const idSuffix: string = this.tenantScheduleQueueActiveJobsSuffix
            .replace('{tenant_id}', tenantId)
            .replace('{schedule_id}', scheduleId.toString());

        return this.httpClient.post<SaveResponse>(`${this.baseUrl}${idSuffix}`, '');
    }

    bulkQueueTenantScheduleJobs(tenantId: string, ingestionIds: number[]) {
        const idSuffix: string = this.tenantV3UrlSuffix.replace('{tenant_id}', tenantId);

        return this.httpClient.post<SaveResponse>(`${this.baseUrl}${idSuffix}/queueJobs`, {
            tenantIngestionIdList: ingestionIds
        });
    }

    activateTenantJobs(tenantId: string, ingestionIds: number[]) {
        const idSuffix: string = this.tenantIngestionsV3UrlSuffix.replace('{tenant_id}', tenantId);

        return this.httpClient.post(`${this.baseUrl}${idSuffix}/activate`, {
            tenantIngestionIdList: ingestionIds
        });
    }

    deactivateTenantJobs(tenantId: string, ingestionIds: number[]) {
        const idSuffix: string = this.tenantIngestionsV3UrlSuffix.replace('{tenant_id}', tenantId);

        return this.httpClient.post(`${this.baseUrl}${idSuffix}/deactivate`, {
            tenantIngestionIdList: ingestionIds
        });
    }

    getTenantIngestionsV1(tenantId: string, ids: number[], query: string) {
        const idSuffix = this.tenantUrlSuffix.replace('{tenant_id}', tenantId);
        return this.httpClient.get<TenantIngestionJobResponse[]>(
            `${this.baseUrl}${idSuffix}/jobs?${query}=` + ids.join(',')
        );
    }

    deleteSingleTenantIngestionJob(ingestion: TenantIngestionIdentifiers) {
        const idSuffix: string = this.tenantSourceEntityV3UrlSuffix
            .replace('{tenant_id}', ingestion.tenantId)
            .replace('{source_id}', ingestion.sourceId.toString())
            .replace('{entity_id}', ingestion.entityId.toString());

        return this.httpClient.delete(`${this.baseUrl}${idSuffix}/ingestions/${ingestion.ingestionId}`);
    }

    deleteMultipleTenantIngestionJobs(ingestions: TenantIngestionIdentifiers) {
        const idSuffix: string = this.tenantSourceEntityV3UrlSuffix
            .replace('{tenant_id}', ingestions.tenantId)
            .replace('{source_id}', ingestions.sourceId.toString())
            .replace('{entity_id}', ingestions.entityId.toString());
        return this.httpClient.post(`${this.baseUrl}${idSuffix}/ingestions/delete`, {
            tenantSourceIngestionIdList: ingestions.ingestionId
        });
    }

    deleteSingleTenantSchedule(tenantId: string, scheduleId: number) {
        const idSuffix = this.tenantScheduleJobsV3UrlSuffix
            .replace('{tenant_id}', tenantId)
            .replace('{schedule_id}', scheduleId.toString());

        return this.httpClient.delete(`${this.baseUrl}${idSuffix}`);
    }

    deleteMultipleTenantSchedules(tenantId: string, scheduleIds: number[]) {
        const idSuffix: string = this.tenantV3UrlSuffix.replace('{tenant_id}', tenantId);

        return this.httpClient.post(`${this.baseUrl}${idSuffix}/schedules/delete`, {
            tenantScheduleIdList: scheduleIds
        });
    }

    deleteSingleEntity(tenantId: string, sourceId: number, entityId: number) {
        const url: string = this.tenantSourceEntityV3UrlSuffix
            .replace('{tenant_id}', tenantId)
            .replace('{source_id}', sourceId.toString())
            .replace('{entity_id}', entityId.toString());

        return this.httpClient.delete(`${this.baseUrl}${url}`);
    }

    deleteMultipleEntities(tenantId: string, sourceId: number, entityIds: number[]) {
        const url: string = this.tenantSourceEntitiesV3UrlSuffix
            .replace('{tenant_id}', tenantId)
            .replace('{source_id}', sourceId.toString());

        return this.httpClient.post(`${this.baseUrl}${url}/delete`, {
            tenantSourceEntityIdList: entityIds
        });
    }

    getUserPermissions() {
        return this.httpClient.get<UserPermissionResponse[]>(
            `${this.baseUrl}${this.tenantV3CreateUrlSuffix}/permissions`
        );
    }

    getTenantSelfHostedIrts(tenantId: string) {
        const idSuffix: string = this.tenantSelfHostedIrtUrlSuffix.replace('{tenant_id}', tenantId);
        return this.httpClient.get<TenantSelfHostedIrt[]>(`${this.baseUrl}${idSuffix}`);
    }

    validateCron(expression: string): Observable<CronValidationResponse> {
        return this.httpClient.post<CronValidationResponse>(`${this.baseUrl}v3/schedules/validation/cron`, {
            expression
        });
    }
}
